import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Loader2, Search, Users, Eye, FileText, Calendar, Filter, X,
  ExternalLink, Mail, AlertCircle,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

/**
 * Recipients — workspace-level dashboard of every email that's opened a
 * gated scorecard share. Useful for sales / legal / partnerships teams
 * to answer "who's been reviewing my contracts?" in one click.
 *
 * Filters: email substring + per-scan filter + page.
 * Stats card: totals across the WHOLE workspace (not the filter), so the
 * user always sees the real numbers even when filtering down.
 */
export default function Recipients() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [scanFilter, setScanFilter] = useState("");
  const [page, setPage] = useState(1);
  // Document filter dropdown — we lazy-load the workspace scan list once.
  const [scanOptions, setScanOptions] = useState([]);

  const pageSize = 25;

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (scanFilter) params.set("scan_id", scanFilter);
      params.set("page", String(page));
      params.set("page_size", String(pageSize));
      const { data: res } = await api.get(`/workspace/recipients?${params.toString()}`);
      setData(res);
    } catch (err) {
      toast.error("Couldn't load recipients", { description: formatApiError(err) });
    } finally {
      setLoading(false);
    }
  };

  // Initial + debounced fetch on filter change. Search box doesn't fire a
  // new request on every keystroke — debounce 350ms.
  useEffect(() => {
    const t = setTimeout(() => { fetchData(); }, q ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, scanFilter, page]);

  // Build the scan filter dropdown from the rows we've already received —
  // we don't want to make a separate /api/scans call just for the picker
  // when most users will only have ~10 documents that ever had a share.
  // For the very first load, we use the rows from the unfiltered request.
  useEffect(() => {
    if (!data?.rows) return;
    setScanOptions((prev) => {
      const map = new Map(prev.map((o) => [o.id, o]));
      for (const r of data.rows) {
        if (!map.has(r.scan_id)) {
          map.set(r.scan_id, { id: r.scan_id, name: r.document_name });
        }
      }
      return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
    });
  }, [data]);

  const totalPages = useMemo(() => {
    if (!data?.total) return 1;
    return Math.max(1, Math.ceil(data.total / pageSize));
  }, [data]);

  const hasFilters = Boolean(q.trim() || scanFilter);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 space-y-6" data-testid="recipients-page">
      <header className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">
            Workspace
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold text-slate-900 mt-1 leading-tight">
            Recipients
          </h1>
          <p className="text-sm text-slate-600 mt-1 max-w-2xl">
            Everyone who's opened a gated scorecard you've shared, across all your
            scans. Filter by email or document to see who's been reviewing which deal.
          </p>
        </div>
      </header>

      {/* Stats ---------------------------------------------------------- */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="recipients-stats">
        <StatCard
          icon={Eye} label="Total views" value={data?.stats?.total_views ?? "—"}
          tint="blue"
        />
        <StatCard
          icon={Mail} label="Unique recipients" value={data?.stats?.unique_recipients ?? "—"}
          tint="emerald"
        />
        <StatCard
          icon={FileText} label="Documents reviewed" value={data?.stats?.unique_documents ?? "—"}
          tint="amber"
        />
        <StatCard
          icon={Calendar}
          label="Most recent view"
          value={
            data?.stats?.last_view_at
              ? relativeTime(data.stats.last_view_at)
              : "Never"
          }
          tint="slate"
        />
      </div>

      {/* Filters -------------------------------------------------------- */}
      <div className="flex flex-col sm:flex-row gap-3" data-testid="recipients-filters">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
          <input
            type="search"
            value={q}
            onChange={(e) => { setQ(e.target.value); setPage(1); }}
            placeholder="Search by email…"
            className="w-full rounded-lg border border-slate-300 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-600/40 focus:border-blue-500"
            data-testid="recipients-search"
          />
        </div>
        <select
          value={scanFilter}
          onChange={(e) => { setScanFilter(e.target.value); setPage(1); }}
          className="rounded-lg border border-slate-300 px-3 py-2 text-sm bg-white sm:w-72 focus:outline-none focus:ring-2 focus:ring-blue-600/40 focus:border-blue-500"
          data-testid="recipients-scan-filter"
        >
          <option value="">All documents</option>
          {scanOptions.map((o) => (
            <option key={o.id} value={o.id}>{o.name}</option>
          ))}
        </select>
        {hasFilters ? (
          <Button
            variant="outline"
            className="h-9 border-slate-300"
            onClick={() => { setQ(""); setScanFilter(""); setPage(1); }}
            data-testid="recipients-clear-filters"
          >
            <X className="h-3.5 w-3.5 mr-1.5" /> Clear
          </Button>
        ) : null}
      </div>

      {/* Table ---------------------------------------------------------- */}
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        {loading && !data ? (
          <div className="py-16 flex items-center justify-center text-sm text-slate-500 gap-2">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading recipients…
          </div>
        ) : data?.rows?.length === 0 ? (
          <EmptyState hasFilters={hasFilters} />
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm" data-testid="recipients-table">
                <thead className="bg-slate-50/60 border-b border-slate-200">
                  <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 font-semibold">
                    <th className="px-5 py-3">Recipient</th>
                    <th className="px-5 py-3">Document</th>
                    <th className="px-5 py-3">Link label</th>
                    <th className="px-5 py-3 text-right">Views</th>
                    <th className="px-5 py-3">Last viewed</th>
                    <th className="px-5 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {(data?.rows || []).map((r) => (
                    <tr
                      key={`${r.email}-${r.share_token}`}
                      className="hover:bg-slate-50 transition-colors"
                      data-testid={`recipients-row-${r.email}`}
                    >
                      <td className="px-5 py-3 align-top">
                        <div className="flex items-center gap-2">
                          <Mail className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                          <span className="font-medium text-slate-900 truncate">
                            {r.email}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3 align-top text-slate-700 max-w-[260px] truncate">
                        <Link
                          to={`/scans/${r.scan_id}`}
                          className="hover:text-blue-700 hover:underline"
                        >
                          {r.document_name}
                        </Link>
                      </td>
                      <td className="px-5 py-3 align-top text-slate-600 text-xs">
                        {r.share_label || <span className="text-slate-400 italic">no label</span>}
                        {!r.share_active && (
                          <span className="ml-2 inline-flex items-center rounded-full bg-slate-100 text-slate-600 px-1.5 py-0.5 text-[10px] font-semibold uppercase">
                            inactive
                          </span>
                        )}
                      </td>
                      <td className="px-5 py-3 align-top text-right tabular-nums font-semibold text-slate-900">
                        {r.view_count}
                      </td>
                      <td className="px-5 py-3 align-top text-slate-700">
                        <div className="text-xs">
                          {relativeTime(r.last_viewed_at)}
                        </div>
                        <div className="text-[10px] text-slate-400">
                          {new Date(r.last_viewed_at).toLocaleString()}
                        </div>
                      </td>
                      <td className="px-5 py-3 align-top text-right">
                        <Link
                          to={`/scans/${r.scan_id}`}
                          className="inline-flex items-center text-xs text-blue-700 hover:text-blue-900"
                          data-testid={`recipients-open-${r.email}`}
                        >
                          Open <ExternalLink className="h-3 w-3 ml-1" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div
                className="flex items-center justify-between px-5 py-3 border-t border-slate-100 bg-slate-50/40"
                data-testid="recipients-pagination"
              >
                <div className="text-xs text-slate-500">
                  Showing {(data.page - 1) * data.page_size + 1}–
                  {Math.min(data.page * data.page_size, data.total)} of {data.total}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 1 || loading}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    data-testid="recipients-prev"
                  >
                    Previous
                  </Button>
                  <span className="text-xs text-slate-500 tabular-nums px-1">
                    Page {data.page} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages || loading}
                    onClick={() => setPage((p) => p + 1)}
                    data-testid="recipients-next"
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tint = "slate" }) {
  const tints = {
    blue:    "bg-blue-50 text-blue-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber:   "bg-amber-50 text-amber-700",
    slate:   "bg-slate-100 text-slate-700",
  };
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-2">
        <span className={`inline-flex h-8 w-8 items-center justify-center rounded-lg ${tints[tint]}`}>
          <Icon className="h-4 w-4" />
        </span>
        <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">
          {label}
        </div>
      </div>
      <div className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">
        {value}
      </div>
    </div>
  );
}

function EmptyState({ hasFilters }) {
  return (
    <div className="py-16 text-center" data-testid="recipients-empty">
      <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-slate-100 text-slate-500">
        <Users className="h-5 w-5" />
      </div>
      <h2 className="mt-4 font-display text-lg font-semibold text-slate-900">
        {hasFilters ? "No matching recipients" : "No recipients yet"}
      </h2>
      <p className="mt-1 text-sm text-slate-500 max-w-sm mx-auto">
        {hasFilters ? (
          "Try clearing your filters to see the full list."
        ) : (
          <>
            When you share a scorecard with the <strong>"Require email"</strong> gate
            enabled, every recipient who opens it shows up here.
          </>
        )}
      </p>
      {!hasFilters && (
        <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-slate-500">
          <AlertCircle className="h-3.5 w-3.5" />
          Open any scan → <span className="font-medium">Share</span> → toggle "Require email to view"
        </div>
      )}
    </div>
  );
}

// Pretty relative-time formatter — keeps the table readable when the user
// is scanning rows ("2h ago" vs full ISO strings). Falls back to date for
// >30 days.
function relativeTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  return d.toLocaleDateString();
}
